import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ChevronRight, ChevronLeft, ShieldCheck, Building2, Wallet, User, FileText, Upload, ClipboardCheck, Rocket, Activity } from "lucide-react";

const STEP_META = [
  { key: "mobile", title: "Mobile Verification", icon: ShieldCheck },
  { key: "personal", title: "Personal Details", icon: User },
  { key: "employment", title: "Employment", icon: Building2 },
  { key: "income", title: "Income", icon: Wallet },
  { key: "identity", title: "Identity", icon: FileText },
  { key: "bank", title: "Bank Details", icon: Building2 },
  { key: "documents", title: "Documents", icon: Upload },
  { key: "review", title: "Review", icon: ClipboardCheck },
  { key: "submit", title: "Submit", icon: Rocket },
  { key: "track", title: "Track", icon: Activity },
];

const DOC_TYPES = [
  { key: "aadhaar", label: "Aadhaar" },
  { key: "pan", label: "PAN" },
  { key: "selfie", label: "Selfie" },
  { key: "salary_slip", label: "Salary Slip" },
  { key: "bank_statement", label: "Bank Statement" },
  { key: "address_proof", label: "Address Proof" },
];

export default function ApplyLoan() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [phone, setPhone] = useState(user?.phone || "");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [kyc, setKyc] = useState({});
  const [emp, setEmp] = useState({});
  const [inc, setInc] = useState({});
  const [bank, setBank] = useState({});
  const [docs, setDocs] = useState({});
  const [loan, setLoan] = useState({ loan_type: "personal", amount: 100000, tenure_months: 12, purpose: "" });
  const [createdLoan, setCreatedLoan] = useState(null);

  useEffect(() => {
    api.get("/kyc").then((r) => setKyc(r.data || {})).catch(() => {});
    api.get("/employment").then((r) => setEmp(r.data || {})).catch(() => {});
    api.get("/income").then((r) => setInc(r.data || {})).catch(() => {});
    api.get("/bank").then((r) => setBank(r.data || {})).catch(() => {});
    api.get("/documents").then((r) => setDocs(Object.fromEntries((r.data || []).map((d) => [d.doc_type, d])))).catch(() => {});
  }, []);

  const progress = ((step + 1) / STEP_META.length) * 100;

  const requestOtp = async () => {
    if (!phone) return toast.error("Enter your mobile number");
    try {
      const r = await api.post("/otp/request", { phone });
      setOtpSent(true);
      toast.success(`${r.data.message}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const verifyOtp = async () => {
    try {
      await api.post("/otp/verify", { phone, otp });
      setOtpVerified(true);
      toast.success("Mobile verified");
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Invalid OTP"); }
  };

  const saveStep = async () => {
    setSaving(true);
    try {
      if (step === 1) await api.put("/kyc", kyc);
      else if (step === 2) await api.put("/employment", emp);
      else if (step === 3) await api.put("/income", inc);
      else if (step === 4) await api.put("/kyc", { aadhaar_no: kyc.aadhaar_no, pan_no: kyc.pan_no });
      else if (step === 5) await api.put("/bank", bank);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await api.post("/documents", { doc_type: docType, file_name: file.name, file_data: String(reader.result).slice(0, 120000) });
        setDocs((d) => ({ ...d, [docType]: { doc_type: docType, file_name: file.name, status: "uploaded" } }));
        toast.success(`${docType} uploaded`);
      } catch (e) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  const submitLoan = async () => {
    setSaving(true);
    try {
      const r = await api.post("/loans/apply", loan);
      setCreatedLoan(r.data);
      toast.success("Application submitted");
      setStep(9);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not submit"); }
    finally { setSaving(false); }
  };

  const next = async () => {
    if (step === 0 && !otpVerified) return toast.error("Verify OTP first");
    if (step >= 1 && step <= 5) await saveStep();
    if (step === 8) return submitLoan();
    setStep((s) => Math.min(STEP_META.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canProceed = useMemo(() => {
    if (step === 0) return otpVerified;
    if (step === 6) return DOC_TYPES.every((d) => docs[d.key]);
    return true;
  }, [step, otpVerified, docs]);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl" data-testid="apply-loan-page">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Loan Application</div>
          <h1 className="font-display text-3xl mt-2">Step {step + 1} of {STEP_META.length} — {STEP_META[step].title}</h1>
          <div className="mt-4">
            <Progress value={progress} className="h-2" data-testid="apply-progress" />
          </div>
        </div>

        {/* Stepper */}
        <div className="grid grid-cols-5 lg:grid-cols-10 gap-2">
          {STEP_META.map((s, i) => {
            const Icon = s.icon;
            const state = i < step ? "done" : i === step ? "current" : "future";
            return (
              <button key={s.key} onClick={() => i < step && setStep(i)} className={`rounded-xl border p-3 text-xs text-left ${state === "current" ? "border-primary bg-primary/5" : state === "done" ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`} data-testid={`step-${s.key}`}>
                <div className="flex items-center gap-2">
                  <div className={`h-6 w-6 rounded-md flex items-center justify-center ${state === "done" ? "brand-gradient text-white" : state === "current" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {state === "done" ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                  </div>
                  <span className="hidden lg:inline">{s.title}</span>
                </div>
              </button>
            );
          })}
        </div>

        <Card className="p-6 lg:p-8 rounded-3xl">
          {step === 0 && (
            <div className="space-y-4 max-w-md">
              <div>
                <Label>Mobile number</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91XXXXXXXXXX" data-testid="apply-phone" />
              </div>
              {!otpSent ? (
                <Button onClick={requestOtp} className="brand-gradient text-white rounded-full" data-testid="apply-request-otp">Send OTP</Button>
              ) : otpVerified ? (
                <div className="text-emerald-600 text-sm flex items-center gap-2"><Check className="h-4 w-4" /> Verified</div>
              ) : (
                <>
                  <div>
                    <Label>Enter OTP (demo: 123456)</Label>
                    <Input value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="apply-otp" />
                  </div>
                  <Button onClick={verifyOtp} className="brand-gradient text-white rounded-full" data-testid="apply-verify-otp">Verify</Button>
                </>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Full name</Label><Input value={kyc.full_name || ""} onChange={(e) => setKyc({ ...kyc, full_name: e.target.value })} data-testid="apply-full-name" /></div>
              <div><Label>Date of birth</Label><Input type="date" value={kyc.dob || ""} onChange={(e) => setKyc({ ...kyc, dob: e.target.value })} data-testid="apply-dob" /></div>
              <div><Label>Gender</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={kyc.gender || ""} onChange={(e) => setKyc({ ...kyc, gender: e.target.value })} data-testid="apply-gender">
                  <option value="">Select</option><option>Female</option><option>Male</option><option>Non-binary</option><option>Prefer not to say</option>
                </select>
              </div>
              <div><Label>City</Label><Input value={kyc.city || ""} onChange={(e) => setKyc({ ...kyc, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={kyc.state || ""} onChange={(e) => setKyc({ ...kyc, state: e.target.value })} /></div>
              <div><Label>Pincode</Label><Input value={kyc.pincode || ""} onChange={(e) => setKyc({ ...kyc, pincode: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Address</Label><Textarea rows={3} value={kyc.address || ""} onChange={(e) => setKyc({ ...kyc, address: e.target.value })} /></div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Employment type</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={emp.employment_type || ""} onChange={(e) => setEmp({ ...emp, employment_type: e.target.value })}>
                  <option value="">Select</option><option>Salaried</option><option>Self-employed</option><option>Freelancer</option>
                </select>
              </div>
              <div><Label>Company</Label><Input value={emp.company_name || ""} onChange={(e) => setEmp({ ...emp, company_name: e.target.value })} data-testid="apply-company" /></div>
              <div><Label>Designation</Label><Input value={emp.designation || ""} onChange={(e) => setEmp({ ...emp, designation: e.target.value })} /></div>
              <div><Label>Work email</Label><Input type="email" value={emp.work_email || ""} onChange={(e) => setEmp({ ...emp, work_email: e.target.value })} /></div>
              <div><Label>Years of experience</Label><Input type="number" value={emp.years_experience || ""} onChange={(e) => setEmp({ ...emp, years_experience: e.target.value })} /></div>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Monthly net income (₹)</Label><Input type="number" value={inc.monthly_income || ""} onChange={(e) => setInc({ ...inc, monthly_income: e.target.value })} data-testid="apply-income" /></div>
              <div><Label>Additional income (₹)</Label><Input type="number" value={inc.additional_income || ""} onChange={(e) => setInc({ ...inc, additional_income: e.target.value })} /></div>
              <div><Label>Existing EMI (₹)</Label><Input type="number" value={inc.existing_emi || ""} onChange={(e) => setInc({ ...inc, existing_emi: e.target.value })} /></div>
              <div><Label>Salary credit date</Label><Input type="number" min={1} max={31} value={inc.salary_date || ""} onChange={(e) => setInc({ ...inc, salary_date: e.target.value })} /></div>
              <div className="md:col-span-2">
                <div className="rounded-xl bg-muted p-4 text-sm">
                  Indicative eligibility: <span className="font-semibold">₹{Math.min(500000, (Number(inc.monthly_income || 0) * 10)).toLocaleString("en-IN")}</span>
                </div>
              </div>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Loan type</Label>
                  <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={loan.loan_type} onChange={(e) => setLoan({ ...loan, loan_type: e.target.value })} data-testid="apply-loan-type">
                    <option value="personal">Personal Loan</option><option value="salary_advance">Salary Advance</option>
                  </select>
                </div>
                <div><Label>Amount</Label><Input type="number" value={loan.amount} onChange={(e) => setLoan({ ...loan, amount: Number(e.target.value) })} data-testid="apply-amount" /></div>
                <div><Label>Tenure (months)</Label><Input type="number" value={loan.tenure_months} onChange={(e) => setLoan({ ...loan, tenure_months: Number(e.target.value) })} data-testid="apply-tenure" /></div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Aadhaar number</Label><Input value={kyc.aadhaar_no || ""} onChange={(e) => setKyc({ ...kyc, aadhaar_no: e.target.value })} placeholder="XXXX XXXX XXXX" data-testid="apply-aadhaar" /></div>
              <div><Label>PAN number</Label><Input value={kyc.pan_no || ""} onChange={(e) => setKyc({ ...kyc, pan_no: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" data-testid="apply-pan" /></div>
              <div className="md:col-span-2 text-xs text-muted-foreground">Your identity numbers are masked in storage and only used for KYC verification.</div>
            </div>
          )}

          {step === 5 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Account holder</Label><Input value={bank.account_holder || ""} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} data-testid="apply-account-holder" /></div>
              <div><Label>Bank name</Label><Input value={bank.bank_name || ""} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} /></div>
              <div><Label>Account number</Label><Input value={bank.account_number || ""} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} data-testid="apply-account-number" /></div>
              <div><Label>IFSC</Label><Input value={bank.ifsc || ""} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} /></div>
              <div><Label>Account type</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={bank.account_type || ""} onChange={(e) => setBank({ ...bank, account_type: e.target.value })}>
                  <option value="">Select</option><option>Savings</option><option>Salary</option><option>Current</option>
                </select>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {DOC_TYPES.map((d) => (
                <div key={d.key} className="rounded-xl border border-border p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{d.label}</div>
                    <div className="text-xs text-muted-foreground">{docs[d.key] ? docs[d.key].file_name : "Not uploaded"}</div>
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadDoc(d.key, e.target.files?.[0])} data-testid={`upload-${d.key}`} />
                    <span className="inline-flex items-center gap-2 rounded-full brand-gradient text-white px-4 py-2 text-xs">{docs[d.key] ? "Replace" : "Upload"}<Upload className="h-3 w-3" /></span>
                  </label>
                </div>
              ))}
            </div>
          )}

          {step === 7 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
              <ReviewBlock title="Personal" items={[["Name", kyc.full_name], ["DOB", kyc.dob], ["City", kyc.city]]} />
              <ReviewBlock title="Employment" items={[["Type", emp.employment_type], ["Company", emp.company_name], ["Role", emp.designation]]} />
              <ReviewBlock title="Income" items={[["Monthly", inc.monthly_income && `₹${Number(inc.monthly_income).toLocaleString("en-IN")}`], ["Existing EMI", inc.existing_emi && `₹${Number(inc.existing_emi).toLocaleString("en-IN")}`]]} />
              <ReviewBlock title="Bank" items={[["Bank", bank.bank_name], ["A/C", bank.account_number && `••${String(bank.account_number).slice(-4)}`]]} />
              <ReviewBlock title="Identity" items={[["Aadhaar", kyc.aadhaar_no && `••${String(kyc.aadhaar_no).slice(-4)}`], ["PAN", kyc.pan_no]]} />
              <ReviewBlock title="Loan" items={[["Type", loan.loan_type.replace("_"," ")], ["Amount", `₹${Number(loan.amount).toLocaleString("en-IN")}`], ["Tenure", `${loan.tenure_months} months`]]} />
            </div>
          )}

          {step === 8 && (
            <div className="text-center py-10 space-y-4">
              <div className="mx-auto h-16 w-16 rounded-2xl brand-gradient text-white flex items-center justify-center"><Rocket className="h-8 w-8" /></div>
              <div className="font-display text-2xl">Ready to submit?</div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">Your application will be reviewed by our credit engine and a human underwriter. Final decision is subject to verification.</p>
              <Button onClick={submitLoan} disabled={saving} className="rounded-full brand-gradient text-white h-11 px-8" data-testid="apply-submit">{saving ? "Submitting..." : "Submit application"}</Button>
            </div>
          )}

          {step === 9 && (
            <div className="text-center py-10 space-y-4">
              <div className="mx-auto h-16 w-16 rounded-2xl brand-gradient text-white flex items-center justify-center"><Activity className="h-8 w-8" /></div>
              <div className="font-display text-2xl">Application submitted!</div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">Application ID: <code>{createdLoan?.id?.slice(0, 8) || "—"}</code></p>
              <div className="flex justify-center gap-3">
                <Button onClick={() => nav("/app/loans")} className="rounded-full brand-gradient text-white" data-testid="apply-track">Track application</Button>
                <Button onClick={() => nav("/app")} variant="outline" className="rounded-full">Go to dashboard</Button>
              </div>
            </div>
          )}

          {step < 8 && (
            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={back} disabled={step === 0} className="rounded-full" data-testid="apply-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              <Button onClick={next} disabled={!canProceed || saving} className="rounded-full brand-gradient text-white" data-testid="apply-next">{saving ? "Saving..." : "Continue"} <ChevronRight className="h-4 w-4 ml-1" /></Button>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}

function ReviewBlock({ title, items }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">{title}</div>
      <div className="space-y-1">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="text-right">{v || "—"}</span></div>
        ))}
      </div>
    </div>
  );
}
